"use client";

import { type RefObject } from "react";
import { CloudUpload, Download, LoaderCircle, Mic } from "lucide-react";
import { Button } from "@speakai/ui/button";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils/cn";
import { FolderSelect } from "@/features/folder/components/folder-select";
import { downloadBlob } from "@/lib/utils/download-blob";
import { extensionForBlob } from "@speakai/common/src/recorder/core/negotiation/mime-extension";
import { formatClock } from "@speakai/common/src/utils/clock";
import { LanguageSelect } from "./language-select";
import { AudioPreviewPlayer } from "./audio-preview-player";
import { PREVIEW_FRAME_CLASS, VIDEO_STYLE } from "./video-style";

const inputClass =
  "w-full rounded-lg border border-input bg-background px-3 py-2 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";

interface RecordingCompleteProps {
  elapsedTime: number;
  mode: "audio" | "video";
  previewUrl: string | null;
  recordingName: string;
  onRecordingNameChange: (name: string) => void;
  folderId: string;
  onFolderChange: (folderId: string) => void;
  sourceLanguage: string;
  onLanguageChange: (lang: string) => void;
  onNewRecording: () => void;
  onUpload: () => void;
  isSubmitting: boolean;
  submitError?: string | null;
  /** Used to derive the correct download extension (.mp4 on Safari, .m4a for audio/mp4). Falls back to .webm. */
  blob?: Blob | null;
  /** Ref forwarded to the playback <audio> or <video> element for setSinkId wiring. */
  mediaRef?: RefObject<HTMLAudioElement | HTMLVideoElement | null>;
  /** True when the take is saved in this browser and can be recovered before upload. */
  isSavedInBrowser?: boolean;
}

export function RecordingComplete({
  elapsedTime,
  mode,
  previewUrl,
  recordingName,
  onRecordingNameChange,
  folderId,
  onFolderChange,
  sourceLanguage,
  onLanguageChange,
  onNewRecording,
  onUpload,
  isSubmitting,
  submitError = null,
  blob = null,
  mediaRef,
  isSavedInBrowser = false,
}: RecordingCompleteProps) {
  const { t } = useTranslation();
  const length = formatClock(elapsedTime, { padMinutes: false });

  return (
    <div
      className="mx-auto flex w-full max-w-[600px] flex-col gap-5"
      data-testid="recorder-complete"
    >
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          {t("RECORD.READY_TO_UPLOAD", "Ready to upload")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isSavedInBrowser
            ? t("RECORD.SAVED_IN_BROWSER_UNTIL_UPLOAD", "{{length}} · Saved in this browser until you upload", { length })
            : t("RECORD.NOT_SAVED_UNTIL_UPLOAD", "{{length}} · Not saved until you upload", { length })}
        </p>
      </div>

      {previewUrl && (
        mode === "video" ? (
          <video
            ref={mediaRef as RefObject<HTMLVideoElement> | undefined}
            controls
            src={previewUrl}
            playsInline
            webkit-playsinline=""
            x-webkit-airplay="deny"
            disablePictureInPicture
            style={VIDEO_STYLE}
            className={cn(PREVIEW_FRAME_CLASS, "rounded-xl bg-foreground/5")}
            data-testid="recorder-video-preview"
          />
        ) : (
          <AudioPreviewPlayer src={previewUrl} duration={elapsedTime} mediaRef={mediaRef} />
        )
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="recording-name" className="text-xs font-medium text-muted-foreground">
          {t("RECORD.NAME", "Recording name")}
        </label>
        <input
          id="recording-name"
          type="text"
          value={recordingName}
          onChange={(e) => onRecordingNameChange(e.target.value)}
          placeholder={t("RECORD.NAME_PLACEHOLDER", "My recording")}
          className={inputClass}
          data-testid="recorder-name-input"
          data-sentry-mask
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <label htmlFor="complete-folder" className="text-xs font-medium text-muted-foreground">
            {t("RECORD.FOLDER", "Save to folder")}
          </label>
          <div data-testid="recorder-folder-select">
            <FolderSelect
              id="complete-folder"
              value={folderId}
              onChange={onFolderChange}
              placeholder={t("RECORD.FOLDER_PLACEHOLDER", "Default")}
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <label htmlFor="complete-language" className="text-xs font-medium text-muted-foreground">
            {t("RECORD.LANGUAGE", "Source language")}
          </label>
          <LanguageSelect
            id="complete-language"
            value={sourceLanguage}
            onChange={onLanguageChange}
            data-testid="recorder-language-input"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Button
          className="w-full gap-2"
          onClick={onUpload}
          disabled={isSubmitting}
          data-testid="recorder-upload"
        >
          {isSubmitting ? (
            <>
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t("RECORD.UPLOADING", "Uploading...")}
            </>
          ) : (
            <>
              <CloudUpload className="h-4 w-4" aria-hidden="true" />
              {t("RECORD.UPLOAD_NOW", "Upload now")}
            </>
          )}
        </Button>
        <Button
          variant="outline"
          className="w-full gap-2"
          onClick={onNewRecording}
          disabled={isSubmitting}
          data-testid="recorder-discard"
        >
          <Mic className="h-4 w-4" aria-hidden="true" />
          {t("RECORD.RECORD_ANOTHER", "Record another")}
        </Button>
      </div>

      {submitError && previewUrl && (
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2"
          onClick={() => {
            if (!blob) return;
            const safeName = (recordingName || `Recording ${new Date().toISOString().slice(0, 19)}`)
              .replace(/[^a-zA-Z0-9._\- ]/g, "_")
              .slice(0, 200);
            downloadBlob(blob, `${safeName}.${extensionForBlob(blob)}`);
          }}
          aria-label={t("RECORD.DOWNLOAD_FALLBACK_ARIA", "Download recording to your computer as a backup")}
          data-testid="recorder-download-fallback"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {t("RECORD.DOWNLOAD_FALLBACK", "Download recording")}
        </Button>
      )}
    </div>
  );
}
